import {memo} from 'react'

import {useParametersTree} from '../../hooks/useParametersTree'
import type {ParameterCategory, ParameterGroup, VirtualizerItem} from '../../Parameters.types'
import {ItemType} from '../../Parameters.types'
import {ParametersCategoryHeader} from '../CategoryHeader/ParametersCategoryHeader'
import {ParametersEmpty} from '../Empty/ParametersEmpty'
import {ParametersGroupDescription} from '../GroupDescription/ParametersGroupDescription'
import {ParametersGroupHeader} from '../GroupHeader/ParametersGroupHeader'
import {ParametersItem} from '../Item/ParametersItem'
import {ParametersTableHeader} from '../TableHeader/ParametersTableHeader'
import {ParametersVirtualizer} from '../Virtualizer/ParametersVirtualizer'

import styles from './ParametersTree.css'

import type {Property} from 'src/services/rest'

interface Props {
  parameters: {
    category: ParameterCategory
    original?: Record<string, string>
    groups: {
      group: ParameterGroup
      withDescription?: boolean
      header?: {
        name?: string
        value?: string
      }
      items: readonly Property[]
    }[]
  }[]
  highlight: string
  isVirtualized: boolean
}

const getItem = (item: VirtualizerItem) => {
  if (item.type === ItemType.CATEGORY_HEADER) {
    return <ParametersCategoryHeader item={item} />
  } else if (item.type === ItemType.GROUP_HEADER) {
    return <ParametersGroupHeader item={item} />
  } else if (item.type === ItemType.TABLE_HEADER) {
    return <ParametersTableHeader item={item} />
  } else if (
    item.type === ItemType.PARAMETER ||
    item.type === ItemType.ADDED_PARAMETER ||
    item.type === ItemType.MODIFIED_PARAMETER
  ) {
    return <ParametersItem item={item} />
  } else if (item.type === ItemType.EMPTY) {
    return <ParametersEmpty />
  } else if (item.type === ItemType.GROUP_DESCRIPTION) {
    return <ParametersGroupDescription item={item} />
  } else {
    return null
  }
}

function ParametersTreeComponent(props: Props) {
  const tree = useParametersTree(props)

  return (
    <div className={styles.page}>
      {props.isVirtualized ? (
        <ParametersVirtualizer tree={tree} getItem={getItem} />
      ) : (
        <div role="treegrid">
          {tree.map(item => (
            <div key={item.key} role="row">
              {getItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const ParametersTree = memo(ParametersTreeComponent)
